import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Shield, Plus, Trash2, Save, X, Loader2, KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";

// ---------- helpers ---------------------------------------------------------

const EMPTY_TENANT = {
  slug: "",
  display_name: "",
  domain: "",
  saml: {
    enabled: false,
    idp_metadata_url: "",
    entity_id: "",
    assertion_consumer_service_url: "",
    name_id_format: "urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress",
    mfa_enforced_by_idp: false,
  },
  oidc: {
    enabled: false,
    provider: "okta",
    server_metadata_url: "",
    client_id: "",
    client_secret: "",
    redirect_uri: "",
    scopes: ["openid", "email", "profile"],
    mfa_enforced_by_idp: false,
  },
  scim: {
    enabled: false,
    auth_type: "bearer",
    bearer_token_hash: null,
    basic_credential_hash: null,
  },
  rbac: {
    group_claim_name: "groups",
    group_to_roles: {},
    default_role: "care_navigator",
  },
};

// ---------- badges ----------------------------------------------------------

function StatusBadge({ ok, label }) {
  return (
    <span
      data-testid={`sso-status-${label.toLowerCase()}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
        ok ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F1F5F9] text-[#64748B]"
      }`}
    >
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <X className="w-3 h-3" />}
      {label}
    </span>
  );
}

// ---------- editor dialog ---------------------------------------------------

function TenantEditor({ initial, onClose, onSaved }) {
  const [t, setT] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isNew = !initial.slug;

  const setNested = (section, key, value) => {
    setT((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));
  };

  const setRoleMapping = (rawJson) => {
    try {
      const parsed = JSON.parse(rawJson);
      setNested("rbac", "group_to_roles", parsed);
      setError("");
    } catch {
      setError("group_to_roles must be valid JSON");
    }
  };

  const save = async () => {
    if (!t.slug || !t.display_name) {
      setError("slug and display_name are required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.put(`/api/sso/admin/tenants/${t.slug}`, t);
      toast.success(`Tenant "${t.slug}" saved`);
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      data-testid="sso-tenant-editor"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-auto"
    >
      <div className="bg-white rounded-2xl w-full max-w-3xl mt-8 border border-[#E5E5E0]">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E0]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#1E3A8A]" />
            </div>
            <div>
              <div className="text-lg font-semibold">
                {isNew ? "New tenant" : `Edit ${initial.slug}`}
              </div>
              <div className="text-xs text-slate-500">SAML / OIDC / SCIM configuration</div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="sso-editor-close"
            className="p-2 rounded-lg hover:bg-slate-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Identity */}
          <section>
            <h4 className="text-sm font-semibold text-[#1E3A8A] mb-3">Identity</h4>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-500">Slug</span>
                <input
                  type="text"
                  value={t.slug}
                  disabled={!isNew}
                  onChange={(e) => setT({ ...t, slug: e.target.value.toLowerCase() })}
                  data-testid="sso-editor-slug"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm disabled:bg-slate-50"
                  placeholder="acme-org"
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Display Name</span>
                <input
                  type="text"
                  value={t.display_name}
                  onChange={(e) => setT({ ...t, display_name: e.target.value })}
                  data-testid="sso-editor-display-name"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
                />
              </label>
              <label className="block col-span-2">
                <span className="text-xs text-slate-500">Email Domain (for discovery)</span>
                <input
                  type="text"
                  value={t.domain || ""}
                  onChange={(e) => setT({ ...t, domain: e.target.value })}
                  data-testid="sso-editor-domain"
                  placeholder="acme.example"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
                />
              </label>
            </div>
          </section>

          {/* SAML */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-[#1E3A8A]">SAML 2.0</h4>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={t.saml.enabled}
                  onChange={(e) => setNested("saml", "enabled", e.target.checked)}
                  data-testid="sso-editor-saml-enabled"
                />
                Enabled
              </label>
            </div>
            <div className="space-y-2">
              <input
                type="url"
                value={t.saml.idp_metadata_url || ""}
                onChange={(e) => setNested("saml", "idp_metadata_url", e.target.value)}
                placeholder="IdP metadata URL (https://…/metadata)"
                data-testid="sso-editor-saml-metadata-url"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
              />
              <input
                type="text"
                value={t.saml.entity_id || ""}
                onChange={(e) => setNested("saml", "entity_id", e.target.value)}
                placeholder="SP Entity ID (e.g. https://harborwell.example)"
                data-testid="sso-editor-saml-entity"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
              />
              <input
                type="url"
                value={t.saml.assertion_consumer_service_url || ""}
                onChange={(e) => setNested("saml", "assertion_consumer_service_url", e.target.value)}
                placeholder="ACS URL (https://harborwell.example/api/sso/{slug}/saml/callback)"
                data-testid="sso-editor-saml-acs"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
              />
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={t.saml.mfa_enforced_by_idp}
                  onChange={(e) => setNested("saml", "mfa_enforced_by_idp", e.target.checked)}
                  data-testid="sso-editor-saml-mfa"
                />
                IdP enforces MFA — skip HarborWell TOTP after this login
              </label>
            </div>
          </section>

          {/* OIDC */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-[#1E3A8A]">OpenID Connect</h4>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={t.oidc.enabled}
                  onChange={(e) => setNested("oidc", "enabled", e.target.checked)}
                  data-testid="sso-editor-oidc-enabled"
                />
                Enabled
              </label>
            </div>
            <div className="space-y-2">
              <select
                value={t.oidc.provider || "okta"}
                onChange={(e) => setNested("oidc", "provider", e.target.value)}
                data-testid="sso-editor-oidc-provider"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm bg-white"
              >
                <option value="okta">Okta</option>
                <option value="azure_ad">Azure AD</option>
                <option value="google_workspace">Google Workspace</option>
                <option value="other">Other (generic OIDC)</option>
              </select>
              <input
                type="url"
                value={t.oidc.server_metadata_url || ""}
                onChange={(e) => setNested("oidc", "server_metadata_url", e.target.value)}
                placeholder="OIDC discovery URL (…/.well-known/openid-configuration)"
                data-testid="sso-editor-oidc-metadata-url"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={t.oidc.client_id || ""}
                  onChange={(e) => setNested("oidc", "client_id", e.target.value)}
                  placeholder="Client ID"
                  data-testid="sso-editor-oidc-client-id"
                  className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
                />
                <input
                  type="password"
                  value={t.oidc.client_secret || ""}
                  onChange={(e) => setNested("oidc", "client_secret", e.target.value)}
                  placeholder="Client Secret (write-only)"
                  data-testid="sso-editor-oidc-client-secret"
                  className="px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
                />
              </div>
              <input
                type="url"
                value={t.oidc.redirect_uri || ""}
                onChange={(e) => setNested("oidc", "redirect_uri", e.target.value)}
                placeholder="Redirect URI (https://…/api/sso/{slug}/oidc/callback)"
                data-testid="sso-editor-oidc-redirect"
                className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
              />
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={t.oidc.mfa_enforced_by_idp}
                  onChange={(e) => setNested("oidc", "mfa_enforced_by_idp", e.target.checked)}
                  data-testid="sso-editor-oidc-mfa"
                />
                IdP enforces MFA — skip HarborWell TOTP after this login
              </label>
            </div>
          </section>

          {/* SCIM */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-[#1E3A8A]">SCIM 2.0 Provisioning</h4>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={t.scim.enabled}
                  onChange={(e) => setNested("scim", "enabled", e.target.checked)}
                  data-testid="sso-editor-scim-enabled"
                />
                Enabled
              </label>
            </div>
            <div className="text-xs text-slate-500 flex items-start gap-2">
              <KeyRound className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                Once enabled, generate a bearer token via the backend and paste
                its <strong>hash</strong> in <code>scim.bearer_token_hash</code>
                (never store the raw token). The IdP uses the raw token to
                authenticate SCIM traffic to
                <code>{" "}/scim/{t.slug || "{slug}"}/v2/Users</code>.
              </span>
            </div>
          </section>

          {/* RBAC mapping */}
          <section>
            <h4 className="text-sm font-semibold text-[#1E3A8A] mb-3">Group → Role Mapping</h4>
            <div className="space-y-2">
              <label className="block">
                <span className="text-xs text-slate-500">Group Claim Name</span>
                <input
                  type="text"
                  value={t.rbac.group_claim_name || "groups"}
                  onChange={(e) => setNested("rbac", "group_claim_name", e.target.value)}
                  data-testid="sso-editor-rbac-claim"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Group → Roles (JSON)</span>
                <textarea
                  rows={5}
                  defaultValue={JSON.stringify(t.rbac.group_to_roles || {}, null, 2)}
                  onBlur={(e) => setRoleMapping(e.target.value)}
                  data-testid="sso-editor-rbac-mapping"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm font-mono text-xs"
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-500">Default Role (if no group matches)</span>
                <select
                  value={t.rbac.default_role || "care_navigator"}
                  onChange={(e) => setNested("rbac", "default_role", e.target.value)}
                  data-testid="sso-editor-rbac-default"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm bg-white"
                >
                  <option value="admin">System Administrator</option>
                  <option value="technical_support">Technical Support</option>
                  <option value="executive_director">Supervisor</option>
                  <option value="care_navigator">Care Navigator</option>
                  <option value="field_specialist">Field Specialist</option>
                  <option value="volunteer">Volunteer</option>
                </select>
              </label>
            </div>
          </section>
        </div>

        {error && (
          <div
            data-testid="sso-editor-error"
            className="mx-6 mb-3 px-3 py-2 rounded-lg bg-[#FEE2E2] text-[#991B1B] text-sm flex items-center gap-2"
          >
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}

        <div className="p-4 border-t border-[#E5E5E0] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="sso-editor-cancel"
            className="px-4 py-2 rounded-lg border border-[#E5E5E0] text-sm hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="sso-editor-save"
            className="px-4 py-2 rounded-lg bg-[#1E3A8A] text-white text-sm hover:bg-[#172554] disabled:opacity-60 flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- main page -------------------------------------------------------

export default function AdminSsoTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/api/sso/admin/tenants");
      setTenants(r.data?.tenants || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const del = async (slug) => {
    if (!window.confirm(`Delete SSO configuration for "${slug}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/sso/admin/tenants/${slug}`);
      toast.success(`Tenant "${slug}" deleted`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-8" data-testid="admin-sso-tenants">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="hw-eyebrow text-[#F59E0B] mb-1">Enterprise SSO</div>
          <h1 className="font-display text-3xl font-semibold text-[#1E3A8A]">
            SSO Tenants
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure SAML, OpenID Connect, and SCIM 2.0 provisioning per partner organisation.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY_TENANT })}
          data-testid="admin-sso-new-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm hover:bg-[#172554]"
        >
          <Plus className="w-4 h-4" />
          New Tenant
        </button>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-[#E5E5E0] overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F9F8F6] text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Slug</th>
              <th className="text-left px-4 py-3">Display Name</th>
              <th className="text-left px-4 py-3">Domain</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E0]">
            {loading && (
              <tr>
                <td colSpan={5} className="text-center py-10 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                  Loading tenants…
                </td>
              </tr>
            )}
            {!loading && tenants.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-10 text-slate-500" data-testid="admin-sso-empty">
                  No SSO tenants configured yet. Click &quot;New Tenant&quot; to add the first one.
                </td>
              </tr>
            )}
            {tenants.map((t) => (
              <tr key={t.slug} data-testid={`admin-sso-row-${t.slug}`}>
                <td className="px-4 py-3 text-sm font-mono">{t.slug}</td>
                <td className="px-4 py-3 text-sm">{t.display_name}</td>
                <td className="px-4 py-3 text-sm text-slate-500">{t.domain || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    <StatusBadge ok={t.saml?.enabled} label="SAML" />
                    <StatusBadge ok={t.oidc?.enabled} label="OIDC" />
                    <StatusBadge ok={t.scim?.enabled} label="SCIM" />
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => setEditing({ ...EMPTY_TENANT, ...t })}
                    data-testid={`admin-sso-edit-${t.slug}`}
                    className="text-sm text-[#1E3A8A] hover:underline mr-3"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => del(t.slug)}
                    data-testid={`admin-sso-delete-${t.slug}`}
                    className="text-sm text-[#DC2626] hover:underline inline-flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <TenantEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}
